import React from 'react'

import {
  AlignItems,
  Body2,
  Box,
  Display,
  Heading2,
  JustifyContent,
  Space,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'

import { CtaLink, mapCmsCta } from '../../cta'
import type { CmsCtaIntentHandlers } from '../../cta'
import { ContentstackImage } from '../ContentstackImage'
import type { ImageWithTextFields } from './types'

export type ImageWithTextProps = ImageWithTextFields & {
  ctaIntentHandlers?: CmsCtaIntentHandlers
}

const ImageRightContainer = withCitrusTheme(Box, (theme) => ({
  ...(theme.responsiveStyle(['column', null, null, 'row'], ['flexDirection']) as Record<
    string,
    unknown
  >),
  margin: '0 auto',
}))

const ImageLeftContainer = withCitrusTheme(Box, (theme) => ({
  ...(theme.responsiveStyle(['column', null, null, 'row-reverse'], ['flexDirection']) as Record<
    string,
    unknown
  >),
  margin: '0 auto',
}))

function getBodyParagraphs(body?: string) {
  if (!body) {
    return []
  }

  return body
    .split(/\n\s*\n/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean)
}

export function ImageWithText({
  heading,
  body,
  image,
  image_mobile,
  image_alt,
  cta,
  image_position,
  ctaIntentHandlers,
}: ImageWithTextProps) {
  const bodyParagraphs = getBodyParagraphs(body)
  const ctaLink = mapCmsCta(cta, ctaIntentHandlers)
  const imageSrc = image?.url ?? image_mobile?.url
  const Container = image_position === 'left' ? ImageLeftContainer : ImageRightContainer

  return (
    <Container
      display={Display.Flex}
      alignItems={AlignItems.FlexStart}
      justifyContent={JustifyContent.SpaceEvenly}
      paddingH={8}
      paddingV={16}
      gap={[8, null, null, 16]}
      maxWidth="1200px"
    >
      <Box
        data-testid="image-with-text-copy"
        width={[null, null, null, imageSrc ? '50%' : '100%']}
        paddingRight={image_position === 'right' ? [null, null, null, 4] : 0}
      >
        <Heading2>{heading}</Heading2>

        {bodyParagraphs.length > 0 && (
          <>
            <Space size={4} />
            {bodyParagraphs.map((paragraph, index) => (
              <React.Fragment key={`${paragraph}-${index}`}>
                {index > 0 && <Space size={4} />}
                <Body2 size={4}>{paragraph}</Body2>
              </React.Fragment>
            ))}
          </>
        )}

        {ctaLink && (
          <>
            <Space size={6} />
            <Box
              display={Display.Flex}
              justifyContent={[JustifyContent.Center, JustifyContent.FlexStart]}
            >
              <CtaLink cta={ctaLink} />
            </Box>
          </>
        )}
      </Box>

      {imageSrc && (
        <Box
          data-testid="image-with-text-image"
          width={['100%', null, null, '50%']}
          style={{ borderRadius: '8px', overflow: 'hidden' }}
        >
          <ContentstackImage
            fit="cover"
            width="100%"
            sizes="(min-width: 1024px) 50vw, 100vw"
            alt={image_alt || ''}
            src={imageSrc}
            mobileSrc={image_mobile?.url}
            mobileMedia="(max-width: 1023px)"
            style={{ display: 'block' }}
          />
        </Box>
      )}
    </Container>
  )
}
